import React, { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { Card, CardBody, Button } from "@material-tailwind/react";
import { FaPenAlt, FaTrash, FaPlus } from 'react-icons/fa';

const { rootUrl } = require('../../../config/config');

// Brand containers per project rules (match PartManufacturers layout)
const Page = ({ children }) => (
    <div className="w-full max-w-7xl mx-auto mt-6 px-2 sm:px-4">
        <Card className="border-t">
            <CardBody className="p-3 sm:p-6 bg-white">{children}</CardBody>
        </Card>
    </div>
);

const api = axios.create({ baseURL: rootUrl });

export default function PartModels() {
    const [items, setItems] = useState([]);
    const [manufacturers, setManufacturers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [units, setUnits] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [error, setError] = useState('');

    const [form, setForm] = useState({
        code: "",
        name: "",
        manufacturer_id: "",
        category_id: "",
        status_id: "",
        uom_id: "",
        model_number: "",
        specs: [],
        description: "",
        input_by: "system",
    });
    const [editingId, setEditingId] = useState(null);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken') || 'default-token';
            const [listRes, manuRes, catRes, unitRes, statusRes, codeRes] = await Promise.all([
                api.get("/parts/models", { headers: { Authorization: `Bearer ${token}` } }),
                api.get("/parts/manufacturers", { headers: { Authorization: `Bearer ${token}` } }),
                api.get("/parts/categories", { headers: { Authorization: `Bearer ${token}` } }),
                api.get("/parts/units", { headers: { Authorization: `Bearer ${token}` } }),
                api.get("/parts/statuses", { headers: { Authorization: `Bearer ${token}` } }),
                api.get("/parts/models/next-code/preview", { headers: { Authorization: `Bearer ${token}` } }),
            ]);
            setItems(listRes.data || []);
            setManufacturers(manuRes.data || []);
            setCategories(catRes.data || []);
            setUnits(unitRes.data || []);
            setStatuses(statusRes.data || []);
            setForm((f) => ({ ...f, code: codeRes.data?.code || "" }));
        } catch (err) {
            console.error(err);
            setError('Failed to load part models.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, []);

    const onChange = (field) => (e) => {
        const value = e?.target ? e.target.value : e;
        setForm((f) => ({ ...f, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => {
                const ne = { ...prev }; delete ne[field]; return ne;
            });
        }
    };

    const onSelectChange = (field) => (selected) => {
        setForm((f) => ({ ...f, [field]: selected ? selected.value : "" }));
        if (errors[field]) {
            setErrors((prev) => {
                const ne = { ...prev }; delete ne[field]; return ne;
            });
        }
    };

    const addSpecRow = () => {
        setForm((f) => ({ ...f, specs: [...f.specs, { key: "", value: "" }] }));
    };

    const updateSpecRow = (index, field, value) => {
        setForm((f) => {
            const newSpecs = [...f.specs];
            newSpecs[index][field] = value;
            return { ...f, specs: newSpecs };
        });
    };

    const removeSpecRow = (index) => {
        setForm((f) => ({ ...f, specs: f.specs.filter((_, i) => i !== index) }));
    };

    const validate = () => {
        let newErrors = {};
        if (!form.name) newErrors.name = "Name is required";
        if (!form.manufacturer_id) newErrors.manufacturer_id = "Manufacturer is required";
        if (!form.category_id) newErrors.category_id = "Category is required";
        if (!form.input_by) newErrors.input_by = "Input By is required";
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return false;
        }
        return true;
    };

    const createItem = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        try {
            // Convert specs array to JSON object
            const specsObject = form.specs.reduce((acc, spec) => {
                if (spec.key && spec.value) {
                    acc[spec.key] = spec.value;
                }
                return acc;
            }, {});

            const payload = {
                name: form.name,
                manufacturer_id: Number(form.manufacturer_id),
                category_id: Number(form.category_id),
                status_id: form.status_id ? Number(form.status_id) : null,
                uom_id: form.uom_id ? Number(form.uom_id) : null,
                model_number: form.model_number || null,
                specs: Object.keys(specsObject).length > 0 ? specsObject : null,
                description: form.description || null,
                input_by: form.input_by,
            };
            const token = localStorage.getItem('authToken') || 'default-token';
            let res;
            if (editingId) {
                // Update existing
                res = await api.put(`/parts/models/${editingId}`, { ...payload, edit_by: form.input_by }, { headers: { Authorization: `Bearer ${token}` } });
                if (res.status === 200) {
                    setEditingId(null);
                }
            } else {
                res = await api.post("/parts/models", payload, { headers: { Authorization: `Bearer ${token}` } });
            }
            setForm((f) => ({ ...f, name: "", manufacturer_id: "", category_id: "", status_id: "", uom_id: "", model_number: "", specs: [], description: "" }));
            await fetchAll();
            // success feedback removed (no inline banners / toasts)
        } catch (err) {
            const msg = err?.response?.data?.error || err?.response?.data?.message || err.message;
            console.error(msg);
            setError(msg || 'Failed to save part model.');
        }
    };

    const startEdit = (item) => {
        setEditingId(item.id);
        // convert specs object -> array if needed
        let specsArr = [];
        try {
            if (item.specs && typeof item.specs === 'object' && !Array.isArray(item.specs)) {
                specsArr = Object.keys(item.specs).map((k) => ({ key: k, value: item.specs[k] }));
            } else if (Array.isArray(item.specs)) {
                specsArr = item.specs;
            }
        } catch (e) { specsArr = []; }
        setForm({
            code: item.code || "",
            name: item.name || "",
            manufacturer_id: item.manufacturer_id || "",
            category_id: item.category_id || "",
            status_id: item.status_id || "",
            uom_id: item.uom_id || "",
            model_number: item.model_number || "",
            specs: specsArr,
            description: item.description || "",
            input_by: item.input_by || form.input_by,
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setForm((f) => ({ ...f, name: "", manufacturer_id: "", category_id: "", status_id: "", uom_id: "", model_number: "", specs: [], description: "" }));
        setErrors({});
    };

    const deleteItem = async (id) => {
        if (!window.confirm('Are you sure you want to delete this part model?')) return;
        try {
            const token = localStorage.getItem('authToken') || 'default-token';
            await api.delete(`/parts/models/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            await fetchAll();
            // success feedback removed
        } catch (err) {
            console.error('Failed to delete part model', err);
            // error feedback removed
        }
    };

    const toggleActive = async (id, isActive) => {
        try {
            const token = localStorage.getItem('authToken') || 'default-token';
            const res = await api.put(`/parts/models/${id}/active`, { is_active: !isActive }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.status === 200) {
                setItems((prev) => prev.map((it) => it.id === id ? { ...it, is_active: !isActive } : it));
                // success feedback removed
            }
        } catch (err) { console.error(err?.response?.data?.error || err.message); }
    };

    // Prepare options for react-select
    const manufacturerOptions = manufacturers
        .filter(m => m.is_active)
        .map(m => ({ value: m.id, label: m.name }));

    const categoryOptions = categories
        .filter(c => c.is_active)
        .map(c => ({ value: c.id, label: c.name }));

    const statusOptions = statuses
        .filter(s => s.is_active)
        .map(s => ({ value: s.id, label: s.name }));

    const uomOptions = units
        .filter(u => u.is_active)
        .map(u => ({ value: u.id, label: `${u.name} (${u.code})` }));

    const getUomLabel = (uomId, fallback) => {
        const u = units.find(x => Number(x.id) === Number(uomId));
        if (u) return u.name || u.code || fallback || '-';
        return fallback || '-';
    };

    const getManufacturerLabel = (manuId, fallback) => {
        const m = manufacturers.find(x => Number(x.id) === Number(manuId));
        if (m) return m.name || fallback || '-';
        return fallback || '-';
    };

    const getCategoryLabel = (catId, fallback) => {
        const c = categories.find(x => Number(x.id) === Number(catId));
        if (c) return c.name || fallback || '-';
        return fallback || '-';
    };

    const customSelectStyles = {
        control: (base) => ({
            ...base,
            borderRadius: '0.5rem',
            borderColor: '#d1d5db',
            padding: '0.125rem',
        }),
        menu: (base) => ({
            ...base,
            zIndex: 50,
        }),
    };

    return (
        <Page>
            <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">Part Models</h1>
                {loading && <span className="text-sm text-gray-500">Loading…</span>}
            </div>

            <form onSubmit={createItem} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {error && (
                    <div className="md:col-span-2 mb-3">
                        <div className="flex items-start justify-between bg-red-50 border border-red-200 text-red-800 px-4 py-2 rounded-none">
                            <div className="flex-1 text-sm">
                                <strong className="block">Error</strong>
                                <div className="mt-1">{error}</div>
                            </div>
                            <button type="button" onClick={() => setError('')} className="ml-4 text-red-600 hover:text-red-800">✕</button>
                        </div>
                    </div>
                )}
                <div>
                    <label className="block text-sm text-gray-700 mb-1">Code</label>
                    <input value={form.code} readOnly className="w-full border rounded-lg px-3 py-2 bg-gray-100" />
                </div>
                <div>
                    <label className="block text-sm text-gray-700 mb-1">Name *</label>
                    <input value={form.name} onChange={onChange("name")} className="w-full border rounded-lg px-3 py-2" />
                    {errors.name && <p className="text-red-600 text-xs mt-1">{errors.name}</p>}
                </div>
                <div>
                    <label className="block text-sm text-gray-700 mb-1">Manufacturer *</label>
                    <Select
                        options={manufacturerOptions}
                        value={manufacturerOptions.find(o => o.value === form.manufacturer_id)}
                        onChange={onSelectChange("manufacturer_id")}
                        styles={customSelectStyles}
                        placeholder="Select manufacturer..."
                        isClearable
                        isSearchable
                    />
                    {errors.manufacturer_id && <p className="text-red-600 text-xs mt-1">{errors.manufacturer_id}</p>}
                </div>
                <div>
                    <label className="block text-sm text-gray-700 mb-1">Category *</label>
                    <Select
                        options={categoryOptions}
                        value={categoryOptions.find(o => o.value === form.category_id)}
                        onChange={onSelectChange("category_id")}
                        styles={customSelectStyles}
                        placeholder="Select category..."
                        isClearable
                        isSearchable
                    />
                    {errors.category_id && <p className="text-red-600 text-xs mt-1">{errors.category_id}</p>}
                </div>
                <div>
                    <label className="block text-sm text-gray-700 mb-1">Status</label>
                    <Select
                        options={statusOptions}
                        value={statusOptions.find(o => o.value === form.status_id)}
                        onChange={onSelectChange("status_id")}
                        styles={customSelectStyles}
                        placeholder="Select status..."
                        isClearable
                        isSearchable
                    />
                </div>
                <div>
                    <label className="block text-sm text-gray-700 mb-1">Unit of Measure</label>
                    <Select
                        options={uomOptions}
                        value={uomOptions.find(o => o.value === form.uom_id)}
                        onChange={onSelectChange("uom_id")}
                        styles={customSelectStyles}
                        placeholder="Select unit..."
                        isClearable
                        isSearchable
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="block text-sm text-gray-700 mb-1">Model Number</label>
                    <input value={form.model_number} onChange={onChange("model_number")} className="w-full border rounded-lg px-3 py-2" />
                </div>
                <div className="md:col-span-2">
                    <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm text-gray-700">Specifications</label>
                        <Button
                            type="button"
                            onClick={addSpecRow}
                            className="flex items-center gap-2 bg-gray-700 hover:bg-gray-800 text-white shadow-none rounded-none px-3 py-1 text-sm"
                            size="sm"
                        >
                            <FaPlus className="w-3 h-3" />
                            Add Spec
                        </Button>
                    </div>
                    <div className="space-y-2 border rounded-lg p-3 bg-gray-50">
                        {form.specs.length === 0 ? (
                            <p className="text-sm text-gray-500 italic">No specifications added</p>
                        ) : (
                            form.specs.map((spec, idx) => (
                                <div key={idx} className="flex gap-2 items-center">
                                    <input
                                        type="text"
                                        placeholder="Key (e.g., voltage)"
                                        value={spec.key}
                                        onChange={(e) => updateSpecRow(idx, "key", e.target.value)}
                                        className="flex-1 border rounded-lg px-3 py-2 text-sm"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Value (e.g., 12V)"
                                        value={spec.value}
                                        onChange={(e) => updateSpecRow(idx, "value", e.target.value)}
                                        className="flex-1 border rounded-lg px-3 py-2 text-sm"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeSpecRow(idx)}
                                        className="px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm"
                                    >✕</button>
                                </div>
                            ))
                        )}
                    </div>
                </div>
                <div className="md:col-span-2">
                    <label className="block text-sm text-gray-700 mb-1">Description</label>
                    <textarea value={form.description} onChange={onChange("description")} rows={3} className="w-full border rounded-lg px-3 py-2" />
                </div>
                <div className="md:col-span-2 flex items-center justify-end gap-3">
                    {editingId && (
                        <button type="button" onClick={cancelEdit} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-none shadow-none">Cancel</button>
                    )}
                    <button type="submit" className="px-4 py-2 bg-brand-primary text-white rounded-none shadow-none hover:bg-[#ff4520]">{editingId ? 'Update Model' : 'Save Model'}</button>
                </div>
            </form>

            <div className="overflow-x-auto -mx-2 sm:mx-0">
                <table className="min-w-full text-xs sm:text-sm border-collapse">
                    <thead>
                        <tr className="text-left border-b bg-gray-100">
                            <th className="border px-2 sm:px-4 py-2 w-32">Code</th>
                            <th className="border px-2 sm:px-4 py-2">Name</th>
                            <th className="border px-2 sm:px-4 py-2 hidden md:table-cell">Manufacturer</th>
                            <th className="border px-2 sm:px-4 py-2 hidden md:table-cell">Category</th>
                            <th className="border px-2 sm:px-4 py-2 hidden lg:table-cell">UoM</th>
                            <th className="border px-2 sm:px-4 py-2">Active</th>
                            <th className="border px-2 sm:px-4 py-2 w-28 text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((it) => (
                            <tr key={it.id} className="border-b hover:bg-gray-50">
                                <td className="border px-2 sm:px-4 py-2 font-mono">{it.code}</td>
                                <td className="border px-2 sm:px-4 py-2">{it.name}</td>
                                <td className="border px-2 sm:px-4 py-2 hidden md:table-cell">{getManufacturerLabel(it.manufacturer_id, it.manufacturer_name)}</td>
                                <td className="border px-2 sm:px-4 py-2 hidden md:table-cell">{getCategoryLabel(it.category_id, it.category_name)}</td>
                                <td className="border px-2 sm:px-4 py-2 hidden lg:table-cell">{getUomLabel(it.uom_id, it.uom_code)}</td>
                                <td className="border px-2 sm:px-4 py-2">
                                    <button
                                        onClick={() => toggleActive(it.id, it.is_active)}
                                        className={`px-2 sm:px-3 py-1 rounded-none shadow-none text-xs font-medium ${it.is_active ? "bg-green-600 text-white" : "bg-gray-300 text-gray-800"}`}
                                    >{it.is_active ? "Active" : "Inactive"}</button>
                                </td>
                                <td className="border px-2 sm:px-4 py-2 text-center">
                                    <div className="flex flex-wrap gap-1 sm:gap-2 justify-center">
                                        <button onClick={() => startEdit(it)} title="Edit" aria-label="Edit" className="bg-blue-600 text-white p-1 sm:p-1.5 shadow-none rounded-none flex items-center justify-center">
                                            <FaPenAlt className="w-2.5 sm:w-3 h-2.5 sm:h-3" />
                                        </button>
                                        <button onClick={() => deleteItem(it.id)} title="Delete" aria-label="Delete" className="bg-red-600 text-white p-1 sm:p-1.5 shadow-none rounded-none flex items-center justify-center">
                                            <FaTrash className="w-2.5 sm:w-3 h-2.5 sm:h-3" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Page>
    );
}
